/**
 * OCR DE UN PDF ESCANEADO, EN UN SOLO SITIO.
 *
 * Vivia dentro de ApplicationsView.jsx, encerrado en el modulo, asi que el
 * IMPORTADOR no lo podia usar: una planilla escaneada (impresa, llenada a
 * mano y pasada por el escaner) no tiene capa de texto, y el importador
 * solo sabia decir "no se pudo extraer la informacion del PDF". Ahora los
 * dos leen por el mismo camino.
 *
 * PRIVACIDAD: tesseract.js es WebAssembly corriendo en el navegador. El
 * documento NUNCA sale del equipo — de la CDN solo bajan la libreria y sus
 * datos de idioma. Por eso esto es compatible con HIPAA. Si algun dia se
 * cambia por un OCR de servidor, deja de serlo.
 */

/**
 * Un PDF cuya capa de texto da menos caracteres que esto es un escaneo (o
 * una exportacion con las fuentes rotas): no hay nada que leer y toca OCR.
 */
export const MINIMO_DE_TEXTO = 60;

/**
 * El OCR es lento (~2-4 s por pagina en WASM). Las aplicaciones ponen sus
 * datos en las primeras paginas.
 */
export const MAXIMO_DE_PAGINAS = 8;

/**
 * Dibuja cada pagina en un canvas y la lee con Tesseract.
 *
 * @param pdf         el documento YA abierto de pdf.js. Importa que sea el
 *                    mismo objeto que se uso para leer la capa de texto:
 *                    pdf.js se queda con el ArrayBuffer, asi que volver a
 *                    abrirlo desde el mismo buffer da un documento vacio.
 * @param onProgress  (pagina, total) mientras corre.
 */
export async function ocrPdfDoc(pdf, onProgress) {
  if (!window.Tesseract) {
    const { loadCdnScript, TESSERACT_5_1_1 } = await import('./cdnVendor.js');
    await loadCdnScript(TESSERACT_5_1_1);
  }
  const pages = Math.min(pdf.numPages, MAXIMO_DE_PAGINAS);
  let text = '';
  for (let i = 1; i <= pages; i++) {
    onProgress?.(i, pages);
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale: 2 });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
    // eng+spa: las hojas de las aseguradoras vienen en los dos idiomas, y
    // una en castellano leida como ingles pierde cada palabra con acento.
    const { data } = await window.Tesseract.recognize(canvas, 'eng+spa');
    text += ((data && data.text) || '') + '\n';
  }
  return text;
}

/** ¿La capa de texto da lo suficiente, o hay que pasar por el OCR? */
export function hayTextoSuficiente(texto) {
  return String(texto || '').replace(/\s+/g, '').length >= MINIMO_DE_TEXTO;
}
